"use client"

import type React from "react"

import { useState, useRef } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card"
import { Alert, AlertDescription } from "./ui/alert"
import { Button } from "./ui/button"
import { CheckCircle2, FileJson, Loader2, Upload, XCircle } from "lucide-react"
import { ApiCalls } from "../../../ApiCalls"
import { ReduxUpdater } from "../../../redux/ReduxUpdater"

export function JsonUploadForm() {
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate it's a JSON file
      if (selectedFile.type === "application/json" || selectedFile.name.endsWith(".json")) {
        setFile(selectedFile);
        setError(null);
        setSuccess(false);
      } else {
        setError("Please select a valid JSON file");
        setFile(null);
      }
    }
  }

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append("file", file);

      // Send to API endpoint
      const response = await ApiCalls.UPLOAD(formData);

      setSuccess(response);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      setError("Failed to upload file. Please try again.")
    } finally {
      ReduxUpdater.reloadLastTransactionDate();
      setUploading(false);
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile && (droppedFile.type === "application/json" || droppedFile.name.endsWith(".json"))) {
      setFile(droppedFile);
      setError(null);
      setSuccess(false);
    } else {
      setError("Please drop a valid JSON file");
    }
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Upload JSON File</CardTitle>
        <CardDescription>Select or drag and drop your JSON file to upload</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
          />
          <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center gap-2">
            <Upload className="w-12 h-12 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              <span className="text-primary font-medium">Click to upload</span> or drag and drop
            </div>
            <div className="text-xs text-muted-foreground">JSON files only</div>
          </label>
        </div>

        {file && (
          <div className="flex items-center gap-2 p-3 bg-secondary rounded-lg">
            <FileJson className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium flex-1">{file.name}</span>
            <span className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(2)} KB</span>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="border-green-500 text-green-700 dark:text-green-400">
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>File uploaded successfully!</AlertDescription>
          </Alert>
        )}

        <Button onClick={handleUpload} disabled={!file || uploading} className={`w-full bg-buttons-${!file || uploading ? 'inactive' : 'primary'} text-white`} size="lg">
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload File
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  )
}
